/**
 * ⌘K / Ctrl+K command palette. Opens above the cockpit, fuzzy-
 * filters a flat command list, executes on Enter or click.
 *
 * Commands are constructed at the call site so they can close over
 * navigate/logout/etc. No registration globbing — every command is
 * explicit. Keep the list curated; the palette should feel fast,
 * not encyclopedic.
 */
import {
  type Component,
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

import { isDeleteChord, pressDelete } from "./paletteDeleteModel";

export interface PaletteCommand {
  id: string;
  label: string;
  hint?: string;
  keywords?: string;
  run: () => void;
  /**
   * Optional destructive action (thread rows). When present the row shows a
   * delete control and ⌘⌫ deletes the highlighted row — guarded by a two-step
   * arm/confirm because the delete is irreversible. See `paletteDeleteModel`.
   */
  onDelete?: () => void | Promise<void>;
}

interface Props {
  open: boolean;
  commands: PaletteCommand[];
  onClose: () => void;
}

const CommandPalette: Component<Props> = (props) => {
  const [query, setQuery] = createSignal("");
  const [highlight, setHighlight] = createSignal(0);
  // Row id currently armed for deletion (awaiting a confirming second ⌘⌫ /
  // click). Null when nothing is armed. Cleared on navigate / query / close.
  const [armedId, setArmedId] = createSignal<string | null>(null);
  let inputEl: HTMLInputElement | undefined;
  let listEl: HTMLUListElement | undefined;

  const filtered = createMemo<PaletteCommand[]>(() => {
    const q = query().trim().toLowerCase();
    if (q.length === 0) return props.commands;
    return props.commands.filter((cmd) => {
      const haystack =
        `${cmd.label} ${cmd.hint ?? ""} ${cmd.keywords ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  });

  // Reset state every time the palette is shown so it always opens
  // with an empty query and the first row highlighted.
  createEffect(() => {
    if (props.open) {
      setQuery("");
      setHighlight(0);
      setArmedId(null);
      // Focus needs to wait one tick for the input to mount.
      queueMicrotask(() => inputEl?.focus());
    }
  });

  // Attempt a delete on `cmd` — shared by the ⌘⌫ chord and the row's delete
  // control. First call arms, second on the same row confirms + fires. Keeps
  // the palette open so the list (refetched by the handler) updates in place.
  const attemptDelete = (cmd: PaletteCommand | undefined) => {
    if (!cmd) return;
    const result = pressDelete(armedId(), {
      id: cmd.id,
      deletable: Boolean(cmd.onDelete),
    });
    setArmedId(result.armedId);
    if (result.fire && cmd.onDelete) {
      void Promise.resolve(cmd.onDelete()).catch(() => {
        // Best-effort: the handler refetches, which resyncs the row if the
        // delete didn't land. No toast UI yet.
      });
    }
  };

  // Clamp the highlight whenever the filtered list shrinks.
  createEffect(() => {
    const max = Math.max(0, filtered().length - 1);
    if (highlight() > max) setHighlight(max);
  });

  const execute = (cmd: PaletteCommand | undefined) => {
    if (!cmd) return;
    props.onClose();
    // Defer so onClose state changes settle before the command runs
    // (some commands navigate, which would race the close otherwise).
    queueMicrotask(() => cmd.run());
  };

  const handleKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      props.onClose();
      return;
    }
    // ⌘⌫ / Ctrl+⌫ (or Delete) deletes the highlighted row, two-step. Checked
    // before the bare-key handlers so the modifier chord isn't swallowed.
    if (isDeleteChord(e)) {
      e.preventDefault();
      attemptDelete(filtered()[highlight()]);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setArmedId(null);
      const max = Math.max(0, filtered().length - 1);
      setHighlight((h) => Math.min(h + 1, max));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setArmedId(null);
      setHighlight((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      execute(filtered()[highlight()]);
    }
  };

  // Keep the highlighted row in view when navigating with arrows.
  createEffect(() => {
    const idx = highlight();
    if (!listEl) return;
    const row = listEl.children[idx] as HTMLElement | undefined;
    row?.scrollIntoView({ block: "nearest" });
  });

  return (
    <Show when={props.open}>
      <div
        class="cmd-palette-backdrop"
        onMouseDown={(e) => {
          // Only close when the user clicks the backdrop itself,
          // not when a click propagates from a child.
          if (e.target === e.currentTarget) props.onClose();
        }}
      >
        <section
          class="cmd-palette"
          aria-label="command palette"
          onKeyDown={handleKey}
        >
          <input
            ref={inputEl}
            class="cmd-palette-input"
            type="text"
            placeholder="type a command — try 'chat', 'compute', 'sign out'…"
            value={query()}
            onInput={(e) => {
              setArmedId(null);
              setQuery(e.currentTarget.value);
            }}
            autocomplete="off"
            spellcheck={false}
          />
          <ul ref={listEl} class="cmd-palette-list">
            <For
              each={filtered()}
              fallback={<li class="cmd-palette-empty">no commands match</li>}
            >
              {(cmd, i) => (
                <li
                  class={`cmd-palette-row${i() === highlight() ? " active" : ""}${
                    armedId() === cmd.id ? " armed" : ""
                  }`}
                  onMouseEnter={() => {
                    // Moving to a different row disarms a pending delete so the
                    // "confirm?" state never lingers on the wrong row.
                    if (cmd.id !== armedId()) setArmedId(null);
                    setHighlight(i());
                  }}
                  onClick={() => execute(cmd)}
                  onKeyDown={() => {
                    // Keyboard navigation (arrows + Enter) is handled
                    // by the parent <section>'s onKeyDown. This handler
                    // exists only to satisfy a11y lints requiring
                    // onClick to pair with a key event.
                  }}
                >
                  <span class="cmd-palette-label">{cmd.label}</span>
                  <Show when={cmd.hint}>
                    <span class="cmd-palette-hint">{cmd.hint}</span>
                  </Show>
                  <Show when={cmd.onDelete}>
                    <button
                      type="button"
                      class="cmd-palette-delete"
                      aria-label={
                        armedId() === cmd.id
                          ? `confirm delete ${cmd.label}`
                          : `delete ${cmd.label}`
                      }
                      title={
                        armedId() === cmd.id
                          ? "click again to delete"
                          : "delete (⌘⌫)"
                      }
                      onClick={(e) => {
                        // Don't let the row's open-on-click fire too.
                        e.stopPropagation();
                        attemptDelete(cmd);
                      }}
                    >
                      {armedId() === cmd.id ? "delete?" : "✕"}
                    </button>
                  </Show>
                </li>
              )}
            </For>
          </ul>
          <footer class="cmd-palette-footer">
            <span>↑↓ navigate</span>
            <span>⏎ select</span>
            <span>⌘⌫ delete</span>
            <span>esc close</span>
          </footer>
        </section>
      </div>
    </Show>
  );
};

// Hook to wire ⌘K / Ctrl+K, `/`, and `?` into a host component.
// `setPaletteOpen` drives the palette state; `setShortcutsOpen` (when
// provided) opens a shortcuts overlay on `?`.
export function useCommandPaletteHotkeys(
  setPaletteOpen: (v: boolean) => void,
  setShortcutsOpen?: (v: boolean) => void,
) {
  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      // ⌘K on Mac, Ctrl+K elsewhere. Match shape used by VS Code,
      // Linear, Notion, etc. so muscle memory transfers.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      // `/` and `?` only fire when the user isn't already typing into
      // an input — otherwise they'd hijack the slash key inside chat,
      // search boxes, etc. Modifier keys are excluded so Cmd+/ etc.
      // remain available for the browser.
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }
      if (e.key === "/") {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (e.key === "?" && setShortcutsOpen) {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });
}

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export default CommandPalette;
